import { definedMap, Writable } from "@glideapps/ts-necessities";
import { getLocalizedString } from "../../components/localization";
import { asMaybeBasePrimitive, asMaybeString } from "../../computation-model/data";
import { TriggerZapBody } from "../../firebase-function-types";

import { ConfirmModalStyle } from "../../components/confirm-modal";
import { SerializedComponent } from "../../components/SerializedApp";
import { defined, definedMapWithDefault, panic } from "../../Support";
import { makeText } from "../builder-utils";
import {
    ArrayPropertyStyle,
    ComponentBuilder,
    ComponentConfiguratorContext,
    WireActionHydrator,
    WireInflationBackend,
} from "../components";
import { makeTextPropertyDescriptorWithSpecialValue } from "../components/descriptor-utils";
import { StringPropertyHandler, ZapPropertyHandler } from "../components/property-handlers";
import { PropertySection } from "../components/property-sections";
import {
    getArrayProperty,
    getColumnProperty,
    makeArrayProperty,
    makeColumnProperty,
} from "../components/property-utils";
import {
    ActionDescription,
    ActionKind,
    InputOutputTables,
    PropertyDescription,
    PropertyKind,
    MutatingScreenKind,
} from "../description";
import { ActionDescriptor } from "./action-descriptor";
import { BaseActionHandler } from "./base";

interface ValueDescription {
    value: PropertyDescription | undefined;
}

interface ZapierActionDescription extends ActionDescription {
    zapID: PropertyDescription;
    values: PropertyDescription | undefined;
    confirmMessage: PropertyDescription | undefined;

    val1: PropertyDescription | undefined;
    val2: PropertyDescription | undefined;
    val3: PropertyDescription | undefined;
}

export class ZapierWithArgsHandler extends BaseActionHandler<ZapierActionDescription> {
    private static zapIDPropertyHandler = new ZapPropertyHandler("zapID", "Zap", PropertySection.Data);

    private static confirmMessagePropertyHandler = new StringPropertyHandler(
        "confirmMessage",
        "Confirm Message",
        "Sent",
        false,
        undefined,
        PropertySection.Data
    );

    public readonly kind = ActionKind.Zapier;

    public readonly iconName = "01-47-flash";

    public getDescriptor(
        _desc: ZapierActionDescription | undefined,
        _tables: InputOutputTables | undefined,
        _ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ActionDescriptor {
        return {
            name: "Trigger Zap",
            needsScreenContext: true,
            properties: [
                ZapierWithArgsHandler.zapIDPropertyHandler,
                {
                    kind: PropertyKind.Array,
                    label: "Values",
                    property: { name: "values" },
                    section: PropertySection.Data,
                    properties: [
                        makeTextPropertyDescriptorWithSpecialValue(
                            "value",
                            i => `Value ${defined(i) + 1}`,
                            "Enter value",
                            true,
                            mutatingScreenKind,
                            {
                                columnFirst: true,
                                isDefaultCaption: true,
                            }
                        ),
                    ],
                    allowEmpty: true,
                    maxItems: 12,
                    allowReorder: true,
                    addItemLabel: "Add value",
                    style: ArrayPropertyStyle.Default,
                },
                ZapierWithArgsHandler.confirmMessagePropertyHandler,
            ],
        };
    }

    public makeAction(
        builder: ComponentBuilder,
        desc: ZapierActionDescription,
        tables: InputOutputTables,
        screenContext: SerializedComponent | undefined
    ): SerializedComponent | undefined {
        const { b } = builder;
        if (screenContext === undefined) {
            return panic("Must have screen context for Zapier action");
        }

        const zapID = ZapierWithArgsHandler.zapIDPropertyHandler.getZap(desc);
        if (zapID === undefined) return undefined;

        const arr = getArrayProperty<ValueDescription>(desc.values) ?? [];
        const values = arr.map(vd =>
            makeText(builder, vd.value, tables.input, false, false, false, false, screenContext)
        );

        const confirmMessage = ZapierWithArgsHandler.confirmMessagePropertyHandler.getString(desc);
        const confirmMessageWithDefaults = definedMapWithDefault(
            confirmMessage,
            b.localizedString("formSubmissionSuccess"),
            n => b.constant(n)
        );

        function getValue(i: number) {
            return values[i] ?? b.constant("");
        }

        return b.actionIfContextDefined(
            b.action("TriggerZapWithValues", {
                zapID: b.constant(zapID),
                val1: getValue(0),
                val2: getValue(1),
                val3: getValue(2),
                val4: getValue(3),
                val5: getValue(4),
                val6: getValue(5),
                val7: getValue(6),
                val8: getValue(7),
                val9: getValue(8),
                val10: getValue(9),
                val11: getValue(10),
                val12: getValue(11),
                onSuccess: b.showToast(confirmMessageWithDefaults, true),
                onFailure: b.showToast(b.localizedString("error"), false),
                onQuotaReached: b.action("ShowPopupModal", {
                    title: b.localizedString("thisActionCouldntBeTriggered"),
                    description: b.localizedString("thisAppMustBeUpgraded"),
                    acceptText: b.localizedString("ok"),
                    cancelText: b.localizedString("support"),
                    style: b.constant(ConfirmModalStyle.QuotaWarning),
                    onAccept: b.noAction(),
                    onCancel: builder.makePushSendAppFeedbackScreen(),
                }),
            })
        );
    }

    public fixActionDescription(desc: Writable<ZapierActionDescription>): void {
        if (desc.values !== undefined) return;

        const columnName1 = getColumnProperty(desc.val1);
        const columnName2 = getColumnProperty(desc.val2);
        const columnName3 = getColumnProperty(desc.val3);

        const values = [columnName1, columnName2, columnName3].map(n => ({ value: definedMap(n, makeColumnProperty) }));

        desc.values = makeArrayProperty(values);
        desc.val1 = undefined;
        desc.val2 = undefined;
        desc.val3 = undefined;
    }

    public inflate(ib: WireInflationBackend, desc: ZapierActionDescription): WireActionHydrator | undefined {
        const { appID, appFacilities } = ib;

        const zapID = ZapierWithArgsHandler.zapIDPropertyHandler.getZap(desc);
        if (zapID === undefined) return undefined;

        const arr = getArrayProperty<ValueDescription>(desc.values) ?? [];
        const getters = arr.map(vd => ib.getValueGetterForProperty(vd.value, false)[0]);

        const [confirmMessageGetter] = ib.getValueGetterForProperty(desc.confirmMessage, true);

        return vp => {
            const values = getters.map(g => asMaybeBasePrimitive(g(vp)));
            const confirmMessage =
                asMaybeString(confirmMessageGetter(vp)) ?? getLocalizedString("formSubmissionSuccess");

            return async ab => {
                const body: TriggerZapBody = {
                    zapID,
                    appID,
                    val1: values[0],
                    val2: values[0],
                    val3: values[0],
                    val4: values[0],
                    val5: values[0],
                    val6: values[0],
                    val7: values[0],
                    val8: values[0],
                    val9: values[0],
                    val10: values[0],
                    val11: values[0],
                    val12: values[0],
                };

                const resp = await appFacilities.callAuthIfAvailableCloudFunction("triggerZap", body, {});

                if (resp?.ok === true) {
                    ab.actionCallbacks.showToast(true, confirmMessage);
                    return true;
                } else if (resp?.status === 402) {
                    ab.actionCallbacks.showToast(
                        false,
                        getLocalizedString("thisActionCouldntBeTriggered") +
                            "\n" +
                            getLocalizedString("thisAppMustBeUpgraded")
                    );
                    return false;
                } else {
                    ab.actionCallbacks.showToast(false, getLocalizedString("error"));
                    return false;
                }
            };
        };
    }
}
